import React, { useCallback, useMemo, useState, useEffect, useRef } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, GetContextMenuItemsParams, MenuItemDef } from 'ag-grid-community';
import { useTranslation } from 'react-i18next';
import { AG_GRID_LOCALE_ES } from '@ag-grid-community/locale';
import { AG_GRID_LOCALE_PT } from '@ag-grid-community/locale';
import { useAuth } from '../auth/AuthContext';
import { clientService, ClientService } from '../../services/clientService';
import { workflowService } from '../../services/api/workflowService';
import { WorkflowState, SPOT_TRANSITIONS, FORWARD_TRANSITIONS } from '../../types/workflow';
import StatusCellRenderer from './StatusCellRenderer';
import AlertModal from '../common/AlertModal';
import ConfirmDialog from '../common/ConfirmDialog';
import UploadingModal from '../common/UploadingModal';
import InlineMenu, { InlineMenuItem } from '../common/InlineMenu';
import AveragingProgressPopup from './AveragingProgressPopup';
import { groupingsService } from '../../services/api/groupingsService';
import { GroupingsConfig } from '../../types/groupings';
import { humanizeFieldName } from '../../utils/fieldNames';
import { logger } from '../../utils/logger';
import { formatTradeDate } from '../../utils/dateCalculations';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import './TradeGrid.css';

// Using v1.0 field structure directly

interface ClientTradesGridProps {
  refreshTrigger?: number;
  newlyCreatedTradeId?: string | null;
  hideUploadControls?: boolean;
  hideOverwriteCheckbox?: boolean; // When true, hide the "Overwrite existing trades" checkbox
  hidePagination?: boolean; // When true, hide the pagination controls at the bottom of the grid
  compactControls?: boolean; // When true, render controls in a compact inline style (for embedded use)
  hideConfirmationColumns?: boolean; // When true, hide Confirmation Received, Confirmation Status, Settlement Instructions columns
  onRowSelected?: (trade: any) => void;
  forwardTradesOnly?: boolean; // When true, only fetch Forward trades (for Contract Comparison)
  workflowStates?: WorkflowState[]; // When provided, only show trades in these workflow states
  menuContext?: 'matching' | 'contracts' | 'booking' | 'none'; // Controls which right-click menu items appear
  onContractUploaded?: (trade: any, file: File) => void;
  includeInternalTrades?: boolean; // When true, include internal trades in results (for Positions page)
  entityFilter?: string; // When set, only show trades where OurPartyId matches this entity ID
  showParentTradeNumber?: boolean; // When true, show Parent Trade Number column (for Positions page)
  multiSelect?: boolean; // When true, allow multi-row selection (for Positions page charts)
  onMultiSelectionChanged?: (trades: any[]) => void; // Callback with all selected rows
  suppressDefaultSort?: boolean; // When true, suppress default ascending sort on TradeNumber
  selectAllTrigger?: number; // Increment to trigger selectAll() on the grid
  clearAllTrigger?: number; // Increment to trigger deselectAll() on the grid
  showMirrorId?: boolean; // When true, show Mirror ID column (for Positions page)
  rowUpdate?: { tradeId: string; fields: Record<string, any> } | null; // Update a single row in-place without full reload
  dateFrom?: string; // YYYY-MM-DD, passed to API for server-side date filtering
  dateTo?: string; // YYYY-MM-DD, passed to API for server-side date filtering
  tradeNumberSearch?: string; // When set, filter rows where TradeNumber OR ParentTradeNumber contains this value
  maturityDateFrom?: string; // YYYY-MM-DD, client-side filter: only show trades with MaturityDate >= this value
  confirmationReceivedFilter?: string; // When set, filter by status field: 'matched' or 'unmatched'
  confirmationStatusFilter?: string; // When set, filter by confirmationStatus field: 'ok' or 'difference'
  includeSplits?: boolean; // When true, include split child trades in API results
  splitViewMode?: 'parents' | 'splits'; // 'parents' = hide isSplitTrade, 'splits' = hide hasSplits parents (avoid double counting)
  onTradesLoaded?: (trades: any[]) => void; // Callback with all loaded trades (raw, unfiltered)
  exportTrigger?: number; // Increment to export currently displayed (sorted/filtered) rows
  onExportData?: (columns: { header: string; field: string }[], rows: any[][]) => void; // Receives column headers + cell values when exportTrigger fires
  clearFiltersTrigger?: number; // Increment to clear all AG Grid column filters
}

const ClientTradesGrid: React.FC<ClientTradesGridProps> = ({
  refreshTrigger,
  newlyCreatedTradeId,
  hideUploadControls = false,
  hideOverwriteCheckbox = false,
  hidePagination = false,
  compactControls = false,
  hideConfirmationColumns = false,
  onRowSelected,
  forwardTradesOnly = false,
  workflowStates,
  menuContext,
  onContractUploaded,
  includeInternalTrades = false,
  entityFilter,
  showParentTradeNumber = false,
  multiSelect = false,
  onMultiSelectionChanged,
  suppressDefaultSort = false,
  selectAllTrigger,
  clearAllTrigger,
  showMirrorId = false,
  rowUpdate = null,
  dateFrom,
  dateTo,
  tradeNumberSearch,
  maturityDateFrom,
  confirmationReceivedFilter,
  confirmationStatusFilter,
  includeSplits = false,
  splitViewMode,
  onTradesLoaded,
  exportTrigger,
  onExportData,
  clearFiltersTrigger,
}) => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();

  const agGridLocale = useMemo(() => {
    const lang = i18n.language?.substring(0, 2);
    if (lang === 'es') return AG_GRID_LOCALE_ES;
    if (lang === 'pt') return AG_GRID_LOCALE_PT;
    return undefined; // English default
  }, [i18n.language]);
  const [trades, setTrades] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadMessage, setUploadMessage] = useState<string | null>(null);
  const [overwriteData, setOverwriteData] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successData, setSuccessData] = useState<{tradesCount: number, message: string} | null>(null);
  const [groupingsConfig, setGroupingsConfig] = useState<GroupingsConfig | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const gridRef = useRef<AgGridReact>(null);
  const onRowSelectedRef = useRef(onRowSelected);
  onRowSelectedRef.current = onRowSelected;

  // External filter for trade number search
  const tradeNumberSearchRef = useRef(tradeNumberSearch);
  tradeNumberSearchRef.current = tradeNumberSearch;
  useEffect(() => {
    gridRef.current?.api?.onFilterChanged();
  }, [tradeNumberSearch]);

  // External filter for maturity date floor
  const maturityDateFromRef = useRef(maturityDateFrom);
  maturityDateFromRef.current = maturityDateFrom;
  useEffect(() => {
    gridRef.current?.api?.onFilterChanged();
  }, [maturityDateFrom]);

  // External filter for confirmation received (status field)
  const confirmationReceivedFilterRef = useRef(confirmationReceivedFilter);
  confirmationReceivedFilterRef.current = confirmationReceivedFilter;
  useEffect(() => {
    gridRef.current?.api?.onFilterChanged();
  }, [confirmationReceivedFilter]);

  // External filter for confirmation status
  const confirmationStatusFilterRef = useRef(confirmationStatusFilter);
  confirmationStatusFilterRef.current = confirmationStatusFilter;
  useEffect(() => {
    gridRef.current?.api?.onFilterChanged();
  }, [confirmationStatusFilter]);

  // External filter for split view mode (parents vs splits)
  const splitViewModeRef = useRef(splitViewMode);
  splitViewModeRef.current = splitViewMode;
  useEffect(() => {
    gridRef.current?.api?.onFilterChanged();
  }, [splitViewMode]);

  // Inline menu state for right-click functionality
  const [showInlineMenu, setShowInlineMenu] = useState(false);
  const [inlineMenuPosition, setInlineMenuPosition] = useState({ x: 0, y: 0 });
  const [selectedTradeForMenu, setSelectedTradeForMenu] = useState<any | null>(null);

  // Averaging progress popup state (Forward Starting averaged-entry)
  const [showAveragingPopup, setShowAveragingPopup] = useState(false);
  const [averagingPopupPosition, setAveragingPopupPosition] = useState({ x: 0, y: 0 });
  const [averagingPopupTrade, setAveragingPopupTrade] = useState<any | null>(null);

  // Contract upload state
  const [uploadingContract, setUploadingContract] = useState(false);
  const [deletingContract, setDeletingContract] = useState(false);
  const contractFileInputRef = useRef<HTMLInputElement>(null);

  // Workflow transition state
  const [processingWorkflow, setProcessingWorkflow] = useState(false);
  const [isDemoMode, setIsDemoMode] = useState(false);

  // Confirm dialog state
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({ isOpen: false, title: '', message: '', onConfirm: () => {} });

  // Workflow alert modal state
  const [workflowAlert, setWorkflowAlert] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: 'info' | 'warning' | 'error' | 'success';
  }>({ isOpen: false, title: '', message: '', type: 'info' });

  // Stabilize workflowStates reference to prevent unnecessary re-renders
  const stableWorkflowStates = useMemo(() => workflowStates, [workflowStates?.join(',')]);

  // Get client ID from user context (assuming user has organization data)
  const clientId = user?.organization?.id || user?.id;

  const loadTrades = useCallback(async (preserveGridState = false, priorityTradeId?: string) => {
    if (!clientId) {
      setLoading(false);
      setError(t('grid.messages.noClientId'));
      return [];
    }

    try {
      if (!preserveGridState) {
        setLoading(true);
      }
      setError(null);

      // Use getForwardTrades for Contract Comparison, getUnmatchedTrades for everything else
      let tradesData = forwardTradesOnly
        ? await clientService.getForwardTrades(clientId, dateFrom, dateTo)
        : await clientService.getUnmatchedTrades(clientId, includeInternalTrades, dateFrom, dateTo, includeSplits);

      // Filter by workflow states if specified
      if (stableWorkflowStates && stableWorkflowStates.length > 0) {
        tradesData = tradesData.filter(trade => {
          const tradeState = (trade as any).workflowState || 'Draft';
          return stableWorkflowStates.includes(tradeState);
        });
      }

      // Filter by entity if specified
      if (entityFilter) {
        tradesData = tradesData.filter(trade => (trade as any).OurPartyId === entityFilter);
      }

      // If we have a priority trade ID, move it to the top
      if (priorityTradeId) {
        const priorityIndex = tradesData.findIndex(trade => trade.id === priorityTradeId);
        if (priorityIndex > -1) {
          const [priorityTrade] = tradesData.splice(priorityIndex, 1);
          tradesData.unshift(priorityTrade);
          logger.info(`Moved trade ${priorityTradeId} from index ${priorityIndex} to top of array`);

          // Clear any active sorting to show the manual order
          if (gridRef.current?.api) {
            const columnState: any = gridRef.current.api;
            if (columnState.applyColumnState) {
              columnState.applyColumnState({
                defaultState: { sort: null }
              });
              logger.info(`Cleared grid sorting to show manual order`);
            }
          }
        } else {
          logger.warn(`Priority trade ${priorityTradeId} not found in trades array`);
        }
      }

      // Always update state - the grid reads from rowData prop which comes from trades state
      setTrades(tradesData);
      if (onTradesLoaded) onTradesLoaded(tradesData);
      return tradesData;
    } catch (error) {
      logger.error('Error loading trades:', error);
      setError(error instanceof Error ? error.message : t('grid.messages.uploadFailed'));
      return [];
    } finally {
      if (!preserveGridState) {
        setLoading(false);
      }
    }
  }, [clientId, t, forwardTradesOnly, stableWorkflowStates, includeInternalTrades, includeSplits, entityFilter, dateFrom, dateTo]);

  // Load trades on mount and when client changes
  useEffect(() => {
    loadTrades();
  }, [loadTrades]);

  // Load workflow config to check demo mode
  useEffect(() => {
    const loadWorkflowConfig = async () => {
      if (!clientId) return;
      try {
        const response = await workflowService.getWorkflowConfig(clientId);
        if (response.success && response.data) {
          setIsDemoMode(response.data.demoMode || false);
        }
      } catch (error) {
        // Silently fail - demo mode defaults to false
        logger.warn('Failed to load workflow config for demo mode check:', error);
      }
    };
    loadWorkflowConfig();
  }, [clientId]);

  // Load groupings config for dynamic column headers
  useEffect(() => {
    const loadGroupingsConfig = async () => {
      if (!clientId) return;
      try {
        const config = await groupingsService.getGroupings(clientId);
        setGroupingsConfig(config);
      } catch (error) {
        logger.warn('Failed to load groupings config:', error);
      }
    };
    loadGroupingsConfig();
  }, [clientId]);

  // Refresh when refreshTrigger changes (called by parent)
  useEffect(() => {
    if (refreshTrigger && refreshTrigger > 0) {
      loadTrades(true, newlyCreatedTradeId || undefined).then((freshTrades) => {
        // If a row is selected, re-fire onRowSelected with fresh data
        if (onRowSelectedRef.current && gridRef.current?.api) {
          const selectedRows = gridRef.current.api.getSelectedRows();
          if (selectedRows.length > 0) {
            const selectedId = selectedRows[0].id;
            const updatedRow = freshTrades.find((t: any) => t.id === selectedId);
            if (updatedRow) {
              onRowSelectedRef.current(updatedRow);
            }
          }
        }
      });
    }
  }, [refreshTrigger, loadTrades, newlyCreatedTradeId]);

  // Select all rows when selectAllTrigger increments
  useEffect(() => {
    if (selectAllTrigger && selectAllTrigger > 0 && gridRef.current?.api) {
      gridRef.current.api.selectAllFiltered();
    }
  }, [selectAllTrigger]);

  // Deselect all rows when clearAllTrigger increments
  useEffect(() => {
    if (clearAllTrigger && clearAllTrigger > 0 && gridRef.current?.api) {
      gridRef.current.api.deselectAll();
    }
  }, [clearAllTrigger]);

  // Clear all column filters when clearFiltersTrigger increments
  useEffect(() => {
    if (clearFiltersTrigger && clearFiltersTrigger > 0 && gridRef.current?.api) {
      gridRef.current.api.setFilterModel(null);
    }
  }, [clearFiltersTrigger]);

  // Export displayed (sorted/filtered) rows when exportTrigger increments
  useEffect(() => {
    if (exportTrigger && exportTrigger > 0 && gridRef.current?.api && onExportData) {
      const api = gridRef.current.api;
      // Get visible columns in display order
      const displayedCols = api.getAllDisplayedColumns();
      const columns = displayedCols.map((col: any) => ({
        header: col.getColDef().headerName || col.getColId(),
        field: col.getColId(),
      }));
      // Build rows using grid's getValue (respects valueGetters, formatters)
      const rows: any[][] = [];
      api.forEachNodeAfterFilterAndSort((node: any) => {
        if (!node.data) return;
        const row = displayedCols.map((col: any) => api.getValue(col, node));
        rows.push(row);
      });
      onExportData(columns, rows);
    }
  }, [exportTrigger, onExportData]);

  // Apply a single row update in-place (no full reload, preserves sort/filter/selection)
  // getRowId ensures AG Grid maintains selection across rowData changes
  useEffect(() => {
    if (!rowUpdate) return;
    const { tradeId, fields } = rowUpdate;

    setTrades(prev => prev.map(t => t.id === tradeId ? { ...t, ...fields } : t));

    // Also update selectedTradeForMenu if it's the same trade (for context menu checks)
    setSelectedTradeForMenu((prev: any) => prev?.id === tradeId ? { ...prev, ...fields } : prev);
  }, [rowUpdate]);

  // Select newly created trade after grid data is loaded
  useEffect(() => {
    if (newlyCreatedTradeId && gridRef.current?.api && trades.length > 0) {
      logger.info(`Selection effect triggered for trade ID: ${newlyCreatedTradeId}`);

      // Check if the trade is at the top (index 0)
      const firstTrade = trades[0];
      if (firstTrade?.id !== newlyCreatedTradeId) {
        logger.info(`Trade ${newlyCreatedTradeId} not at top yet, waiting for refresh...`);
        return;
      }

      logger.info(`Trade ${newlyCreatedTradeId} is at top, selecting it`);

      // Wait for grid to be ready and data to be rendered
      setTimeout(() => {
        const api = gridRef.current?.api;
        if (!api) return;

        // Deselect all
        api.deselectAll();

        // Select the first row
        const firstNode = api.getDisplayedRowAtIndex(0);
        if (firstNode) {
          logger.info(`First node ID: ${firstNode.data?.id}, Expected: ${newlyCreatedTradeId}`);
          if (firstNode.data?.id === newlyCreatedTradeId) {
            firstNode.setSelected(true);
            api.ensureIndexVisible(0, 'top');
            logger.info(`✅ Selected trade ${newlyCreatedTradeId} at top of grid`);
          } else {
            logger.warn(`❌ First row is not the expected trade. Got: ${firstNode.data?.id}`);
          }
        } else {
          logger.warn(`❌ No first node found in grid`);
        }
      }, 300);
    }
  }, [newlyCreatedTradeId, trades]);

  // Add native context menu handler for any grid cell
  // Only attach if menuContext is not 'none'
  useEffect(() => {
    // Skip context menu entirely if menuContext is 'none'
    if (menuContext === 'none') {
      return;
    }

    const handleContextMenu = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      // Check if the clicked element is within any grid cell
      const cellElement = target.closest('.ag-cell');

      if (cellElement) {
        e.preventDefault();
        e.stopPropagation();

        // Find the row node to get the data
        const rowElement = cellElement.closest('[role="row"]');
        if (rowElement && gridRef.current?.api) {
          const rowIndex = rowElement.getAttribute('row-index');
          if (rowIndex) {
            const rowNode = gridRef.current.api.getDisplayedRowAtIndex(parseInt(rowIndex));
            if (rowNode) {
              // Select the row when right-clicking
              rowNode.setSelected(true, true); // Select and clear other selections

              // Detect Price column click on averaged-entry Forward Starting trades
              const colId = cellElement.getAttribute('col-id');
              const tradeData = rowNode.data;
              if (
                colId === 'Price' &&
                tradeData?.IsForwardStarting &&
                tradeData?.ForwardStartingFixingSchedule?.length > 1
              ) {
                setShowInlineMenu(false);
                setAveragingPopupTrade(tradeData);
                setAveragingPopupPosition({ x: e.clientX, y: e.clientY });
                setShowAveragingPopup(true);
                return;
              }

              setSelectedTradeForMenu(rowNode.data);
              setInlineMenuPosition({ x: e.clientX, y: e.clientY });
              setShowInlineMenu(true);
            }
          }
        }
      }
    };

    // Add the event listener
    document.addEventListener('contextmenu', handleContextMenu);

    // Cleanup
    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
    };
  }, [trades, menuContext]); // Re-attach when trades or menuContext change
  
  const columnDefs: ColDef[] = useMemo(() => {
    const allColumns: ColDef[] = [
    {
      headerName: t('grid.columns.confirmationReceived'),
      field: 'status',  // Keep this as 'status' to read the correct data field
      colId: 'trade_status',  // Use a unique column ID to avoid conflicts
      width: 130,
      sortable: true,
      filter: true,
      filterValueGetter: (params) => {
        const status = params.data?.status;
        return status === 'matched' ? t('grid.values.yes') : t('grid.values.no');
      },
      cellRenderer: (params: any): React.ReactElement => {
        const status = params.value;
        // Show 'Yes' if matched, 'No' otherwise
        const isMatched = status === 'matched';
        const displayValue = isMatched ? t('grid.values.yes') : t('grid.values.no');
        const color = isMatched ? '#4CAF50' : '#FFFFFF';

        return React.createElement('div', {
          style: { color: color, fontWeight: 'bold', textAlign: 'center', width: '100%' }
        }, displayValue);
      }
    },
    {
      headerName: t('grid.columns.confirmationStatus'),
      field: 'confirmationStatus',
      colId: 'confirmationStatus',
      width: 140,
      sortable: true,
      filter: true,
      filterValueGetter: (params) => {
        const confirmationStatus = params.data?.confirmationStatus;
        if (!confirmationStatus) return '';
        if (confirmationStatus === 'ok') return t('grid.values.ok');
        if (confirmationStatus === 'provisional') return t('grid.values.provisional', 'Provisional');
        return t('grid.values.difference');
      },
      cellRenderer: (params: any): React.ReactElement => {
        const confirmationStatus = params.value;
        // Only show if there's a value (trade has been matched)
        if (!confirmationStatus) {
          return React.createElement('div', {}, '');
        }

        let displayValue: string;
        let color: string;
        if (confirmationStatus === 'ok') {
          displayValue = t('grid.values.ok');
          color = '#4CAF50';
        } else if (confirmationStatus === 'provisional') {
          displayValue = t('grid.values.provisional', 'Provisional');
          color = '#64B5F6'; // light blue
        } else {
          displayValue = t('grid.values.difference');
          color = '#ff4444';
        }

        return React.createElement('div', {
          style: { color: color, fontWeight: 'bold', textAlign: 'center', width: '100%' }
        }, displayValue);
      }
    },
    {
      headerName: t('grid.columns.settlementInstruction'),
      field: 'settlementInstructionStatus',
      colId: 'settlementInstructionStatus',
      width: 120,
      sortable: true,
      filter: true,
      filterValueGetter: (params) => {
        const settlementStatus = params.data?.settlementInstructionStatus;
        return settlementStatus === 'generated' ? t('grid.values.yes') : t('grid.values.no');
      },
      cellRenderer: (params: any): React.ReactElement => {
        const settlementStatus = params.value;

        // Show icon based on status
        if (!settlementStatus) {
          return React.createElement('div', {}, '');
        }

        let icon = '';
        let color = '#b3b3b3';

        if (settlementStatus === 'generated') {
          icon = '✓';
          color = '#4CAF50';
        } else if (settlementStatus === 'error') {
          icon = '✗';
          color = '#ff4444';
        }

        return React.createElement('div', {
          style: { color: color, fontWeight: 'bold', textAlign: 'center', fontSize: '16px' }
        }, icon);
      }
    },
    {
      headerName: t('grid.columns.workflow'),
      field: 'workflowState',
      width: 140,
      sortable: true,
      filter: true,
      valueGetter: (params) => {
        const state = params.data?.workflowState || 'Draft';
        const pending = params.data?.pendingApproval;
        if (pending?.targetState) {
          return `${pending.fromState || state} → ${pending.targetState}`;
        }
        return state;
      },
      cellRenderer: (params: any): React.ReactElement => {
        const hasException = params.data?.hasWorkflowExceptions === true;
        const blockedAuto = params.data?.blockedAutoTransition;
        const text = params.valueFormatted || params.value || '';
        const children: React.ReactNode[] = [text];

        if (hasException) {
          children.push(
            React.createElement('span', {
              key: 'exc',
              title: t('trades.exceptions.indicatorTooltip', 'One or more workflow transitions were automatically executed via a configured exception rule.'),
              style: {
                marginLeft: '4px',
                color: '#ffffff',
                fontWeight: 'bold',
                cursor: 'help',
              }
            }, '*')
          );
        }

        // KAN-199: Blocked auto-transition indicator
        if (blockedAuto) {
          const missingNames = (blockedAuto.missingFields || []).map((f: string) => t(`tradeFields.${f}`, humanizeFieldName(f))).join(', ');
          const tooltip = t('trades.transitionBlock.blockedIndicatorTooltip',
            'Auto-transition {{transition}} blocked by rule "{{ruleName}}". Missing fields: {{fields}}. Fill in these fields to allow the trade to advance.',
            { transition: blockedAuto.transition, ruleName: blockedAuto.ruleName, fields: missingNames }
          );
          children.push(
            React.createElement('span', {
              key: 'blocked',
              title: tooltip,
              style: {
                marginLeft: '4px',
                color: '#f59e0b',
                cursor: 'help',
              }
            }, '\u26A0')
          );
        }

        return React.createElement('span', {}, children);
      }
    },
    {
      headerName: t('grid.columns.approval'),
      field: 'pendingApproval',
      width: 90,
      sortable: true,
      filter: true,
      valueGetter: (params) => {
        const pending = params.data?.pendingApproval;
        if (pending) {
          const current = pending.currentApprovals?.length || 0;
          const required = pending.requiredApprovers || 1;
          return `${current}/${required}`;
        }
        return '';
      }
    },
    {
      headerName: t('grid.columns.tradeNumber'),
      field: 'TradeNumber',
      width: 90,
      sortable: true,
      filter: true,
      // TEMPORARILY DISABLED: default ascending sort on TradeNumber
      // ...(newlyCreatedTradeId || suppressDefaultSort ? {} : { sort: 'asc' as const, sortIndex: 0 })
    },
    ...(showParentTradeNumber ? [{
      headerName: t('grid.columns.parentTradeNumber'),
      valueGetter: (params: any) => params.data?.internalTradeRef?.parentTradeNumber || '',
      width: 110,
      sortable: true,
      filter: true,
    }] : []),
    ...(showMirrorId ? [{
      headerName: t('grid.columns.mirrorId'),
      field: 'MirrorId',
      width: 90,
      sortable: true,
      filter: true,
    }] : []),
    {
      headerName: t('grid.columns.entity'),
      field: 'OurParty',
      width: 140,
      sortable: true,
      filter: true
    },
    {
      headerName: t('grid.columns.bankTradeNumber'),
      field: 'bankTradeNumber',
      width: 130,
      sortable: true,
      filter: true
    },
    {
      headerName: t('grid.columns.counterparty'),
      field: 'CounterpartyName',
      width: 140,
      sortable: true,
      filter: true
    },
    {
      headerName: t('grid.columns.productType'),
      width: 130,
      sortable: true,
      filter: true,
      valueGetter: (params: any) => {
        const product = params.data?.ProductType || '';
        if (product === 'Forward') {
          if (params.data?.IsForwardStarting) return 'Forward (Starting)';
          if (params.data?.IsAverageRateForward) return 'Forward (Asiático)';
        }
        return product;
      }
    },
    { 
      headerName: t('grid.columns.tradeDate'), 
      field: 'TradeDate', 
      width: 130,
      sortable: true, 
      filter: true,
      valueFormatter: (params) => formatTradeDate(params.value)
    },
    {
      headerName: t('grid.columns.valueDate'),
      field: 'ValueDate',
      width: 130,
      sortable: true,
      filter: true,
      valueFormatter: (params) => formatTradeDate(params.value)
    },
    { 
      headerName: t('grid.columns.direction'), 
      field: 'Direction', 
      width: 70, 
      sortable: true, 
      filter: true 
    },
    { 
      headerName: t('grid.columns.currency1'), 
      field: 'Currency1', 
      width: 70, 
      sortable: true, 
      filter: true 
    },
    { 
      headerName: t('grid.columns.amount'), 
      field: 'QuantityCurrency1', 
      width: 130, 
      sortable: true, 
      filter: true, 
      valueFormatter: (params) => {
        const value = params.value || 0;
        return value.toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        });
      }
    },
    {
      headerName: t('grid.columns.price'),
      field: 'Price',
      width: 100,
      sortable: true,
      filter: true,
      valueFormatter: (params) => {
        if (params.data?.IsForwardStarting && (!params.value || params.value === 0)) {
          return t('tradeBooking.forwardStarting.toBeFixed');
        }
        const value = params.value || 0;
        return value.toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        });
      },
      cellStyle: (params) => params.data?.IsForwardStarting && (!params.value || params.value === 0)
        ? { backgroundColor: 'rgba(245, 158, 11, 0.15)', color: '#b45309' } : undefined,
    },
    {
      headerName: t('grid.columns.spotPrice'),
      field: 'SpotPrice',
      width: 100,
      sortable: true,
      filter: true,
      valueFormatter: (params) => {
        if (params.data?.IsForwardStarting && (!params.value || params.value === 0)) {
          return t('tradeBooking.forwardStarting.toBeFixed');
        }
        if (params.value == null) return '';
        return params.value.toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 4
        });
      },
      cellStyle: (params) => params.data?.IsForwardStarting && (!params.value || params.value === 0)
        ? { backgroundColor: 'rgba(245, 158, 11, 0.15)', color: '#b45309' } : undefined,
    },
    {
      headerName: t('grid.columns.forwardPoints'),
      field: 'ForwardPoints',
      width: 110,
      sortable: true,
      filter: true,
      valueFormatter: (params) => {
        if (params.value == null) return '';
        return params.value.toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 4
        });
      }
    },
    {
      headerName: t('grid.columns.currency2'),
      field: 'Currency2',
      width: 70,
      sortable: true,
      filter: true
    },
    {
      headerName: t('grid.columns.contraAmount'),
      field: 'QuantityCurrency2',
      width: 130,
      sortable: true,
      filter: true,
      valueFormatter: (params) => {
        if (params.data?.IsForwardStarting && (!params.value || params.value === 0)) {
          return t('tradeBooking.forwardStarting.toBeFixed');
        }
        const value = params.value || 0;
        return value.toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        });
      },
      cellStyle: (params) => params.data?.IsForwardStarting && (!params.value || params.value === 0)
        ? { backgroundColor: 'rgba(245, 158, 11, 0.15)', color: '#b45309' } : undefined,
    },
    {
      headerName: t('grid.columns.maturityDate'), 
      field: 'MaturityDate', 
      width: 130,
      sortable: true, 
      filter: true,
      valueFormatter: (params) => formatTradeDate(params.value)
    },
    {
      headerName: t('grid.columns.fixingReference'),
      field: 'FixingReference',
      width: 120,
      sortable: true,
      filter: true
    },
    {
      headerName: t('grid.columns.settlementType'),
      field: 'SettlementType',
      width: 130,
      sortable: true,
      filter: true
    },
    {
      headerName: t('grid.columns.settlementCurrency'),
      field: 'SettlementCurrency',
      width: 120,
      sortable: true,
      filter: true
    },
    {
      headerName: t('grid.columns.paymentDate'),
      field: 'PaymentDate',
      width: 130,
      sortable: true,
      filter: true,
      valueFormatter: (params) => formatTradeDate(params.value)
    },
    {
      headerName: t('grid.columns.currency1PaymentDate'),
      field: 'Currency1PaymentDate',
      width: 160,
      sortable: true,
      filter: true,
      valueFormatter: (params) => formatTradeDate(params.value)
    },
    {
      headerName: t('grid.columns.currency2PaymentDate'),
      field: 'Currency2PaymentDate',
      width: 160,
      sortable: true,
      filter: true,
      valueFormatter: (params) => formatTradeDate(params.value)
    },
    {
      headerName: t('grid.columns.fixingOffset'),
      field: 'FixingOffset',
      width: 100,
      sortable: true,
      filter: true,
      valueFormatter: (params) => {
        if (params.value === null || params.value === undefined || params.value === '' || params.value === 'N/A') {
          return 'N/A';
        }
        const numValue = Number(params.value);
        if (isNaN(numValue)) {
          return 'N/A';
        }
        return String(numValue);
      }
    },
    {
      headerName: groupingsConfig?.grouping1Label || t('grid.columns.grouping1'),
      field: 'Grouping1',
      width: 120,
      sortable: true,
      filter: true
    },
    {
      headerName: groupingsConfig?.grouping2Label || t('grid.columns.grouping2'),
      field: 'Grouping2',
      width: 120,
      sortable: true,
      filter: true
    },
    {
      headerName: groupingsConfig?.grouping3Label || t('grid.columns.grouping3'),
      field: 'Grouping3',
      width: 120,
      sortable: true,
      filter: true
    },
    {
      headerName: t('grid.columns.hedgeObject'),
      field: 'HedgeObject',
      width: 120,
      sortable: true,
      filter: true
    },
    {
      headerName: t('grid.columns.legacyReference1'),
      field: 'LegacyReference1',
      width: 120,
      sortable: true,
      filter: true
    },
    {
      headerName: t('grid.columns.legacyReference2'),
      field: 'LegacyReference2',
      width: 120,
      sortable: true,
      filter: true
    },
  ];

    // Hide Approval column on Confirmations page — not relevant for matching context
    if (menuContext === 'matching') {
      const approvalIdx = allColumns.findIndex(col => col.field === 'pendingApproval');
      if (approvalIdx >= 0) allColumns.splice(approvalIdx, 1);
    }

    // Filter out confirmation-related columns if hideConfirmationColumns is true (Contracts page)
    if (hideConfirmationColumns) {
      const columnsToHide = ['trade_status', 'confirmationStatus', 'settlementInstructionStatus'];
      const filtered = allColumns.filter(col => !columnsToHide.includes(col.colId || ''));

      // Prepend contract-specific columns before Trade Number
      const hasContractCol: ColDef = {
        headerName: t('grid.columns.hasContract'),
        field: 'ContractUrl',
        colId: 'hasContract',
        width: 90,
        sortable: true,
        filter: true,
        filterValueGetter: (params) => {
          return params.data?.ContractUrl ? t('grid.values.yes') : t('grid.values.no');
        },
        cellRenderer: (params: any): React.ReactElement => {
          const hasContract = !!params.value;
          return React.createElement('div', {
            style: { color: hasContract ? '#4CAF50' : 'transparent', fontWeight: 'bold', textAlign: 'center', fontSize: '16px' }
          }, hasContract ? '✓' : '');
        }
      };

      const contractStatusCol: ColDef = {
        headerName: t('grid.columns.contractComparisonStatus'),
        field: 'ContractComparisonStatus',
        colId: 'contractComparisonStatus',
        width: 130,
        sortable: true,
        filter: true,
        filterValueGetter: (params) => {
          const compStatus = params.data?.ContractComparisonStatus;
          if (!compStatus) return '';
          if (compStatus === 'Contract OK') return t('grid.values.contractOk');
          if (compStatus === 'Difference') return t('grid.values.difference');
          if (compStatus === 'Needs Review') return t('grid.values.needsReview');
          return compStatus;
        },
        cellRenderer: (params: any): React.ReactElement => {
          const compStatus = params.value;
          if (!compStatus) {
            return React.createElement('div', {}, '');
          }
          let color = '#FFFFFF';
          let displayValue = compStatus;
          if (compStatus === 'Contract OK') { color = '#4CAF50'; displayValue = t('grid.values.contractOk'); }
          else if (compStatus === 'Difference') { color = '#ff4444'; displayValue = t('grid.values.difference'); }
          else if (compStatus === 'Needs Review') { color = '#FFA500'; displayValue = t('grid.values.needsReview'); }

          return React.createElement('div', {
            style: { color, fontWeight: 'bold', textAlign: 'center', width: '100%' }
          }, displayValue);
        }
      };

      // Insert contract columns after Approval, before Trade Number
      const approvalIndex = filtered.findIndex(col => col.field === 'pendingApproval');
      const insertAt = approvalIndex >= 0 ? approvalIndex + 1 : 0;
      filtered.splice(insertAt, 0, hasContractCol, contractStatusCol);
      return filtered;
    }

    return allColumns;
  }, [t, newlyCreatedTradeId, hideConfirmationColumns, groupingsConfig, showParentTradeNumber, menuContext]);

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !clientId) {
      if (!clientId) setUploadMessage(t('grid.messages.noClientId'));
      return;
    }

    // Validate file type
    if (!file.name.toLowerCase().endsWith('.csv')) {
      setUploadMessage(t('grid.messages.selectCsvFile'));
      return;
    }

    setUploading(true);
    setUploadMessage(null);

    try {
      // Use existing clientService method with overwrite parameter
      const result = await clientService.uploadTradeFileWithOverwrite(clientId, file, overwriteData);
      
      // Refresh the trades data while preserving grid state
      await loadTrades(true);
      
      // Show success modal
      setSuccessData({
        tradesCount: result.records_processed,
        message: `${t('grid.messages.successfullyProcessed').replace('{count}', result.records_processed.toString())}`
      });
      setShowSuccessModal(true);
      setUploadMessage(null); // Clear any previous messages
      
    } catch (error) {
      logger.error('Upload error:', error);
      setUploadMessage(error instanceof Error ? error.message : t('grid.messages.uploadFailed'));
      setShowSuccessModal(false);
    } finally {
      setUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const triggerFileUpload = () => {
    fileInputRef.current?.click();
  };

  const handleDeleteAllTrades = async () => {
    if (!clientId) {
      setUploadMessage(t('grid.messages.noClientId'));
      return;
    }

    setConfirmDialog({
      isOpen: true,
      title: t('grid.messages.deleteTradesTitle', 'Delete Trades'),
      message: t('grid.messages.confirmDeleteTrades'),
      onConfirm: async () => {
        setConfirmDialog(prev => ({ ...prev, isOpen: false }));
        await performDeleteTrades();
      },
    });
  };

  const performDeleteTrades = async () => {
    if (!clientId) return;

    setDeleting(true);
    setUploadMessage(null);

    try {
      // Use the real delete endpoint
      const result = await clientService.deleteAllUnmatchedTrades(clientId);
      
      // Refresh the trades data from database while preserving grid state
      await loadTrades(true);
      
      setSuccessData({
        tradesCount: result.trades_deleted,
        message: `${t('grid.messages.successfullyDeleted').replace('{count}', result.trades_deleted.toString())}`
      });
      setShowSuccessModal(true);
      setUploadMessage(null);
      
    } catch (error) {
      logger.error('Delete error:', error);
      setUploadMessage(error instanceof Error ? error.message : t('grid.messages.deleteFailed'));
      setShowSuccessModal(false);
    } finally {
      setDeleting(false);
    }
  };

  const handleContractUpload = useCallback(async (file: File, trade: any) => {
    if (!clientId) return;

    setUploadingContract(true);
    try {
      const tradeOrgId = trade.organizationId as string;
      await clientService.uploadTradeContract(tradeOrgId, trade.id, file);
      // Refresh trades to show updated contract
      const freshTrades = await loadTrades(true);
      logger.info(`Contract uploaded for trade ${trade.TradeNumber}`);

      // Find the updated trade from the freshly fetched data
      const updatedTrade = freshTrades.find((t: any) => t.id === trade.id) || trade;

      // Notify parent with updated trade data so selectedTrade has ContractUrl
      // (getRowId preserves selection, so onSelectionChanged won't re-fire)
      if (onRowSelected) {
        onRowSelected(updatedTrade);
      }

      // Notify parent to auto-preview the uploaded contract
      if (onContractUploaded) {
        onContractUploaded(updatedTrade, file);
      }
    } catch (error) {
      logger.error('Contract upload failed:', error);
      setWorkflowAlert({ isOpen: true, title: 'Error', message: error instanceof Error ? error.message : 'Unknown error', type: 'error' });
    } finally {
      setUploadingContract(false);
    }
  }, [clientId, loadTrades, onContractUploaded]);

  // Contract delete handler
  const handleContractDelete = useCallback(async (trade: any) => {
    if (!clientId) return;

    setConfirmDialog({
      isOpen: true,
      title: t('contractComparison.deleteContract'),
      message: t('grid.messages.confirmDeleteContract', { tradeNumber: trade.TradeNumber }),
      onConfirm: async () => {
        setConfirmDialog(prev => ({ ...prev, isOpen: false }));

        setDeletingContract(true);
        try {
          const tradeOrgId = trade.organizationId as string;
          await clientService.deleteTradeContract(tradeOrgId, trade.id);
          await loadTrades(true);
          logger.info(`Contract deleted for trade ${trade.TradeNumber}`);

          if (onRowSelected) {
            onRowSelected({ ...trade, ContractUrl: undefined, SignatureStatus: undefined, SignatureRequestId: undefined, SignedDocumentUrl: undefined });
          }
        } catch (error) {
          logger.error('Contract delete failed:', error);
          setWorkflowAlert({ isOpen: true, title: 'Error', message: error instanceof Error ? error.message : 'Unknown error', type: 'error' });
        } finally {
          setDeletingContract(false);
        }
      },
    });
  }, [clientId, loadTrades, onRowSelected, t]);

  // Workflow transition handler
  const handleWorkflowTransition = useCallback(async (trade: any, targetState: WorkflowState) => {
    if (!clientId) return;

    setProcessingWorkflow(true);
    let pendingAlert: typeof workflowAlert | null = null;

    try {
      const tradeOrgId = trade.organizationId as string;
      const response = await workflowService.requestTransition(
        tradeOrgId,
        trade.id,
        targetState
      );

      if (response.success) {
        if (response.data?.pendingApproval) {
          // Transition requires approval - queue alert for after spinner closes
          pendingAlert = {
            isOpen: true,
            title: t('workflow.messages.approvalRequired'),
            message: t('workflow.messages.pendingApproval', {
              state: targetState,
              required: response.data.requiredApprovers
            }),
            type: 'info'
          };
        } else {
          // Transition completed immediately
          logger.info(`Trade ${trade.TradeNumber} transitioned to ${targetState}`);
        }
        // Refresh trades to show updated workflow state
        const freshTrades = await loadTrades(true);
        // Re-fire onRowSelected with fresh data so parent panels update
        // (e.g. Confirmations page clears email/comparison panels on revert)
        if (onRowSelectedRef.current && trade.id) {
          const updatedTrade = freshTrades.find((t: any) => t.id === trade.id);
          if (updatedTrade) {
            onRowSelectedRef.current(updatedTrade);
          }
        }
      } else {
        throw new Error(response.message || 'Failed to process workflow transition');
      }
    } catch (error: any) {
      logger.error('Workflow transition failed:', error);
      // KAN-199: Detect structured transition block error
      if (error?.detail?.blocked) {
        const missingFields = (error.detail.missingFields || []) as string[];
        const fieldNames = missingFields.map(f => t(`tradeFields.${f}`, humanizeFieldName(f))).join(', ');
        pendingAlert = {
          isOpen: true,
          title: t('trades.transitionBlock.modalTitle', 'Transition Blocked'),
          message: t('trades.transitionBlock.modalMessage',
            'The following fields must be completed before this transition can proceed: {{fields}}. Please edit the trade to fill in the required fields, then try the transition again.',
            { fields: fieldNames }
          ),
          type: 'warning'
        };
      } else {
        const errorMsg = error instanceof Error ? error.message : (error?.message || error?.detail || 'Unknown error');
        // KAN-205: Same-day guard — show translated message
        const isSameDayGuard = typeof errorMsg === 'string' && errorMsg.includes('previous business day');
        // KAN-222: Delete matched trade guard — show translated message
        const isDeleteMatchedGuard = typeof errorMsg === 'string' && errorMsg.includes('active confirmation match');
        pendingAlert = {
          isOpen: true,
          title: t('common.error'),
          message: isSameDayGuard
            ? t('workflow.messages.sameDayGuardError')
            : isDeleteMatchedGuard
            ? t('workflow.messages.deleteMatchedGuardError')
            : t('workflow.messages.transitionError', { error: errorMsg }),
          type: 'error'
        };
      }
    } finally {
      setProcessingWorkflow(false);
      // Show alert after spinner is dismissed
      if (pendingAlert) {
        setWorkflowAlert(pendingAlert);
      }
    }
  }, [clientId, loadTrades, t]);

  // Workflow approval handler
  const handleWorkflowApproval = useCallback(async (trade: any) => {
    if (!clientId) return;

    setProcessingWorkflow(true);
    let pendingAlert: typeof workflowAlert | null = null;

    try {
      const tradeOrgId = trade.organizationId as string;
      const response = await workflowService.approveTransition(tradeOrgId, trade.id);

      if (response.success) {
        if (response.data?.pendingApproval) {
          // More approvals needed - queue alert for after spinner closes
          pendingAlert = {
            isOpen: true,
            title: t('workflow.messages.approvalRecordedTitle'),
            message: t('workflow.messages.approvalRecorded', {
              current: response.data.currentApprovers,
              required: response.data.requiredApprovers
            }),
            type: 'success'
          };
        } else {
          // All approvals collected, transition completed
          pendingAlert = {
            isOpen: true,
            title: t('common.success'),
            message: t('workflow.messages.transitionComplete', {
              state: trade.pendingApproval?.targetState || 'completed'
            }),
            type: 'success'
          };
          logger.info(`Trade ${trade.TradeNumber} approval completed`);
        }
        // Refresh trades to show updated workflow state
        await loadTrades(true);
      } else {
        throw new Error(response.message || 'Failed to approve transition');
      }
    } catch (error) {
      logger.error('Workflow approval failed:', error);
      pendingAlert = {
        isOpen: true,
        title: t('common.error'),
        message: t('workflow.messages.approvalError', {
          error: error instanceof Error ? error.message : 'Unknown error'
        }),
        type: 'error'
      };
    } finally {
      setProcessingWorkflow(false);
      // Show alert after spinner is dismissed
      if (pendingAlert) {
        setWorkflowAlert(pendingAlert);
      }
    }
  }, [clientId, loadTrades, t]);

  // Get valid workflow transitions for a trade
  const getValidTransitions = useCallback((trade: any): WorkflowState[] => {
    const currentState = trade.workflowState as WorkflowState || 'Draft';
    const productType = trade.ProductType?.toLowerCase();

    // Terminal states have no transitions
    if (currentState === 'Final' || currentState === 'Cancelled' || currentState === 'Unwound') {
      return [];
    }

    const transitions = productType === 'forward' ? FORWARD_TRANSITIONS : SPOT_TRANSITIONS;
    return transitions
      .filter(t => t.fromState === currentState)
      .map(t => t.toState)
      .filter(state => !(state === 'Draft' && trade.SourceChannel === 'FXGO'));
  }, []);

  const getContextMenuItems = useCallback((params: GetContextMenuItemsParams): (string | MenuItemDef)[] => [
    {
      name: t('grid.contextMenu.viewDetails'),
      action: () => {/* View details action */}
    },
    {
      name: t('grid.contextMenu.edit'),
      action: () => {/* Edit trade action */}
    },
    {
      name: t('grid.contextMenu.delete'),
      action: () => {/* Delete trade action */}
    },
    'separator',
    {
      name: t('grid.contextMenu.duplicate'),
      action: () => {/* Duplicate trade action */}
    }
  ], [t]);

  if (loading) {
    return (
      <div className="ag-theme-alpine-dark trade-grid loading-state">
        <div className="grid-spinner" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="ag-theme-alpine-dark trade-grid error-state">
        <div className="error-message">
          {t('grid.messages.errorLoading')}: {error}
          <button 
            onClick={() => window.location.reload()} 
            className="retry-button"
          >
            {t('grid.messages.retry')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ height: '100%', width: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      {/* Upload Controls */}
      {!hideUploadControls && (
        <div className={`grid-header-controls ${compactControls ? 'compact' : ''}`} style={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          ...(compactControls ? {
            borderBottom: 'none',
            marginTop: '0',
            marginBottom: '0',
            padding: '0'
          } : {
            borderBottom: '1px solid #333',
            marginTop: '-16px',
            marginBottom: '8px'
          })
        }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {uploadMessage && (
              <div style={{
                color: uploadMessage.includes('Successfully') ? '#28a745' : '#dc3545',
                fontSize: '14px',
                marginRight: '15px'
              }}>
                {uploadMessage}
              </div>
            )}

            <div className="upload-controls" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <button
                onClick={handleDeleteAllTrades}
                disabled={uploading || deleting || trades.length === 0}
                title={deleting ? t('grid.messages.deleting') : t('grid.messages.deleteAllUnmatched')}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '36px',
                  height: '36px',
                  background: 'transparent',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: (uploading || deleting || trades.length === 0) ? 'not-allowed' : 'pointer',
                  opacity: (uploading || deleting || trades.length === 0) ? 0.5 : 1,
                  transition: 'opacity 0.2s ease'
                }}
              >
                {deleting ? (
                  <span style={{ fontSize: '16px' }}>⏳</span>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="#4a9eff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ width: '16px', height: '16px' }}>
                    <path d="M3 6h18" />
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
                    <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                  </svg>
                )}
              </button>

              <button
                onClick={triggerFileUpload}
                disabled={uploading || deleting}
                title={uploading ? t('grid.messages.uploading') : t('grid.messages.uploadCsvFile')}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '36px',
                  height: '36px',
                  background: 'transparent',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: (uploading || deleting) ? 'not-allowed' : 'pointer',
                  opacity: (uploading || deleting) ? 0.5 : 1,
                  transition: 'opacity 0.2s ease'
                }}
              >
                {uploading ? (
                  <span style={{ fontSize: '16px' }}>⏳</span>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="#4a9eff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ width: '16px', height: '16px' }}>
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="17 8 12 3 7 8" />
                    <line x1="12" y1="3" x2="12" y2="15" />
                  </svg>
                )}
              </button>

              <button
                onClick={() => loadTrades(true)}
                disabled={uploading || deleting}
                title={t('grid.messages.refreshData') || 'Refresh data'}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '36px',
                  height: '36px',
                  background: 'transparent',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: (uploading || deleting) ? 'not-allowed' : 'pointer',
                  opacity: (uploading || deleting) ? 0.5 : 1,
                  transition: 'opacity 0.2s ease'
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="#4a9eff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ width: '16px', height: '16px' }}>
                  <polyline points="23 4 23 10 17 10" />
                  <polyline points="1 20 1 14 7 14" />
                  <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
                </svg>
              </button>

              {!hideOverwriteCheckbox && (
                <label style={{ display: 'flex', alignItems: 'center', gap: '5px', color: '#fff', fontSize: '14px', whiteSpace: 'nowrap' }}>
                  <input
                    type="checkbox"
                    checked={overwriteData}
                    onChange={(e) => setOverwriteData(e.target.checked)}
                    disabled={uploading || deleting}
                  />
                  {t('grid.messages.overwriteExistingTrades')}
                </label>
              )}

              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileUpload}
                style={{ display: 'none' }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Grid Content */}
      {trades.length === 0 ? (
        <div className="empty-state" style={{ flex: '1', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#b3b3b3' }}>
          {t('grid.messages.noTradesFound')}
        </div>
      ) : (
        <div className="ag-theme-alpine-dark trade-grid" style={{ flex: '1', minHeight: 0, height: '100%' }}>
          <AgGridReact
            ref={gridRef}
            rowData={trades}
            columnDefs={columnDefs}
            localeText={agGridLocale}
            getRowId={(params) => params.data.id}
            pagination={!hidePagination}
            paginationPageSize={hidePagination ? undefined : 50}
            suppressMovableColumns={false}
            domLayout="normal"
            rowSelection={multiSelect ? "multiple" : "single"}
            onSelectionChanged={(event) => {
              const selectedRows = event.api.getSelectedRows();
              if (onRowSelected && selectedRows.length > 0) {
                onRowSelected(selectedRows[0]);
              }
              if (onMultiSelectionChanged) {
                onMultiSelectionChanged(selectedRows);
              }
            }}
            getRowClass={(params) => {
              if (params.data?.isInternalTrade) return 'internal-trade-row';
              return undefined;
            }}
            defaultColDef={{
              sortable: true,
              filter: true,
              resizable: true,
              minWidth: 80,
              filterParams: {
                buttons: ['reset'],
                textMatcher: ({ value, filterText, filterOption }: { value: string; filterText: string; filterOption: string }) => {
                  const normalize = (s: string) => (s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
                  const nVal = normalize(value);
                  const nFilter = normalize(filterText);
                  switch (filterOption) {
                    case 'contains': return nVal.includes(nFilter);
                    case 'notContains': return !nVal.includes(nFilter);
                    case 'equals': return nVal === nFilter;
                    case 'notEqual': return nVal !== nFilter;
                    case 'startsWith': return nVal.startsWith(nFilter);
                    case 'endsWith': return nVal.endsWith(nFilter);
                    default: return nVal.includes(nFilter);
                  }
                }
              }
            }}
            sortingOrder={['asc', 'desc']}
            isExternalFilterPresent={() => !!tradeNumberSearchRef.current || !!maturityDateFromRef.current || !!confirmationReceivedFilterRef.current || !!confirmationStatusFilterRef.current || !!splitViewModeRef.current}
            doesExternalFilterPass={(node) => {
              // Trade number filter
              const search = tradeNumberSearchRef.current;
              if (search) {
                const tradeNum = String(node.data?.TradeNumber || '');
                const parentNum = String(node.data?.internalTradeRef?.parentTradeNumber || node.data?.parentTradeNumber || '');
                if (tradeNum !== search && parentNum !== search) return false;
              }
              // Maturity date floor filter
              const minDate = maturityDateFromRef.current;
              if (minDate) {
                const raw = node.data?.MaturityDate || '';
                if (!raw) return false;
                // Normalize to YYYY-MM-DD for comparison
                const parts = raw.split('-');
                const normalized = parts[0].length === 4 ? raw : `${parts[2]}-${parts[1]}-${parts[0]}`;
                if (normalized < minDate) return false;
              }
              // Confirmation received filter (status field: matched/unmatched)
              const confRecFilter = confirmationReceivedFilterRef.current;
              if (confRecFilter) {
                const status = node.data?.status || '';
                if (status !== confRecFilter) return false;
              }
              // Confirmation status filter (confirmationStatus field: ok/difference)
              const confStatusFilter = confirmationStatusFilterRef.current;
              if (confStatusFilter) {
                const confStatus = node.data?.confirmationStatus || '';
                if (confStatus !== confStatusFilter) return false;
              }
              // Split view mode filter (avoid double counting)
              const svm = splitViewModeRef.current;
              if (svm === 'parents' && node.data?.isSplitTrade) return false;
              if (svm === 'splits' && node.data?.hasSplits) return false;
              return true;
            }}
          />
        </div>
      )}
      
      {/* Success Modal */}
      {showSuccessModal && successData && (
        <AlertModal
          isOpen={showSuccessModal}
          onClose={() => setShowSuccessModal(false)}
          title={t('grid.messages.uploadSuccessful')}
          message={successData.message}
          type="success"
        />
      )}

      {/* Workflow Alert Modal */}
      <AlertModal
        isOpen={workflowAlert.isOpen}
        onClose={() => setWorkflowAlert(prev => ({ ...prev, isOpen: false }))}
        title={workflowAlert.title}
        message={workflowAlert.message}
        type={workflowAlert.type}
      />

      {/* Inline Menu for right-click */}
      {showInlineMenu && selectedTradeForMenu && (
        <InlineMenu
          position={inlineMenuPosition}
          onClose={() => setShowInlineMenu(false)}
          items={(() => {
            const menuItems: (InlineMenuItem | 'separator')[] = [];

            // Filter menu items based on menuContext
            // 'booking' context only shows workflow options (no matching or contracts)
            const showMatching = menuContext === 'matching' || menuContext === undefined;
            const showContracts = menuContext === 'contracts' || menuContext === undefined;
            const showWorkflow = menuContext !== 'none';

            // For matching/contracts contexts (Confirmations/Contracts pages), only BO and Admin can perform actions
            // FO-only users can view but not interact on these back-office pages
            const isBackOfficeContext = menuContext === 'matching' || menuContext === 'contracts';
            const hasBackOfficeRole = ['client_admin', 'client_bo_user', 'client_fo_bo_user'].includes(user?.role || '');
            const canPerformBackOfficeActions = !isBackOfficeContext || hasBackOfficeRole;

            // Internal trades are read-only — no workflow, edit, or delete actions permitted
            if (selectedTradeForMenu.isInternalTrade) {
              return [{
                label: t('grid.contextMenu.internalTradeReadOnly'),
                action: () => setShowInlineMenu(false),
                disabled: true,
                className: 'info-text',
              }];
            }

            // Add separator if there are existing items and we're adding contract options
            if (menuItems.length > 0 && showContracts && canPerformBackOfficeActions) {
              menuItems.push('separator');
            }

            // Contract upload/delete options (contract workflow)
            if (showContracts && canPerformBackOfficeActions) {
              if (selectedTradeForMenu.ContractUrl) {
                // If contract exists, only show Delete (disabled if signature in progress or complete)
                const signatureActive = selectedTradeForMenu.SignatureStatus === 'PENDING' || selectedTradeForMenu.SignatureStatus === 'SIGNED';
                menuItems.push({
                  label: t('contractComparison.deleteContract'),
                  action: () => {
                    setShowInlineMenu(false);
                    handleContractDelete(selectedTradeForMenu);
                  },
                  disabled: deletingContract || signatureActive
                });
              } else {
                // If no contract, only show Upload
                menuItems.push({
                  label: t('contractComparison.uploadContract'),
                  action: () => {
                    setShowInlineMenu(false);
                    contractFileInputRef.current?.click();
                  },
                  disabled: uploadingContract
                });
              }
            }

            // Workflow transition options (show for matching, contracts, booking contexts)
            // For matching/contracts contexts, also require BO/Admin role
            if (showWorkflow && canPerformBackOfficeActions) {
              let validTransitions = getValidTransitions(selectedTradeForMenu)
                .filter(state => state !== 'Deleted');  // Deleted is handled separately via Delete Draft menu item
              const hasPendingApproval = !!selectedTradeForMenu.pendingApproval;
              const currentState = selectedTradeForMenu.workflowState || 'Draft';

              // For booking context: Draft→Booked is removed (must use Trade Booking page
              // to go through quote approval gate). Booked→Draft (revert) is still allowed.
              // Draft trades get a "Delete Draft" option instead.
              if (menuContext === 'booking') {
                if (currentState === 'Draft') {
                  validTransitions = [];
                  // Add "Delete Draft" menu item
                  if (menuItems.length > 0) menuItems.push('separator');
                  menuItems.push({
                    label: t('workflow.actions.deleteDraft'),
                    action: () => {
                      setShowInlineMenu(false);
                      setConfirmDialog({
                        isOpen: true,
                        title: t('workflow.actions.deleteDraft'),
                        message: t('workflow.messages.confirmDeleteDraft', {
                          tradeNumber: selectedTradeForMenu.TradeNumber || selectedTradeForMenu.tradeNumber || ''
                        }),
                        onConfirm: () => {
                          setConfirmDialog(prev => ({ ...prev, isOpen: false }));
                          handleWorkflowTransition(selectedTradeForMenu, 'Deleted' as any);
                        },
                      });
                    },
                    disabled: processingWorkflow || hasPendingApproval
                  });
                } else if (currentState === 'Booked') {
                  validTransitions = validTransitions.filter(state => state === 'Draft');
                } else {
                  // Not in Draft or Booked state - no transitions available in booking context
                  validTransitions = [];
                }
              }

              // For matching context (Confirmations page), only allow Booked↔Confirmed transitions
              if (menuContext === 'matching') {
                if (currentState === 'Booked') {
                  validTransitions = validTransitions.filter(state => state === 'Confirmed');
                } else if (currentState === 'Confirmed') {
                  validTransitions = validTransitions.filter(state => state === 'Booked');
                } else {
                  // Not in Booked or Confirmed state - no transitions available in matching context
                  validTransitions = [];
                }
              }

              // For contracts context (Contracts page), only allow Confirmed↔ContractSigned↔Final transitions
              if (menuContext === 'contracts') {
                // Block all workflow transitions while a signature is pending
                if (selectedTradeForMenu.SignatureStatus === 'PENDING') {
                  validTransitions = [];
                } else if (currentState === 'Confirmed') {
                  validTransitions = validTransitions.filter(state => state === 'ContractSigned');
                } else if (currentState === 'ContractSigned') {
                  validTransitions = validTransitions.filter(state => state === 'Final' || state === 'Confirmed');
                } else {
                  // Not in Confirmed or ContractSigned state - no transitions available in contracts context
                  validTransitions = [];
                }
              }

              // Add separator if we have previous items and workflow options
              if (menuItems.length > 0 && (validTransitions.length > 0 || hasPendingApproval)) {
                menuItems.push('separator');
              }

              // If trade has pending approval, show approve option (if user can approve)
              if (hasPendingApproval) {
                const pendingTo = selectedTradeForMenu.pendingApproval?.targetState;
                const currentApprovals = selectedTradeForMenu.pendingApproval?.currentApprovals || [];
                const userAlreadyApproved = currentApprovals.some(
                  (a: { userId: string }) => a.userId === user?.id
                );
                // Check if current user requested this transition (segregation of duties)
                const requestedBy = selectedTradeForMenu.pendingApproval?.requestedBy;
                const userRequestedTransition = requestedBy === user?.id;
                // In demo mode, allow same user to approve their own requests
                const canApprove = isDemoMode || (!userAlreadyApproved && !userRequestedTransition);

                // Check if the pending approval is relevant to the current page context
                let approvalRelevantToContext = true;
                if (menuContext === 'booking') {
                  // Booking page: only show approvals for Draft↔Booked transitions
                  approvalRelevantToContext = pendingTo === 'Booked' || pendingTo === 'Draft';
                } else if (menuContext === 'matching') {
                  // Confirmations page: only show approvals for Booked↔Confirmed transitions
                  approvalRelevantToContext = pendingTo === 'Confirmed' || pendingTo === 'Booked';
                } else if (menuContext === 'contracts') {
                  // Contracts page: only show approvals for Confirmed↔ContractSigned↔Final transitions
                  approvalRelevantToContext = pendingTo === 'ContractSigned' || pendingTo === 'Confirmed' || pendingTo === 'Final';
                }

                // Only show approve option if user can approve AND it's relevant to this page
                if (canApprove && approvalRelevantToContext) {
                  menuItems.push({
                    label: t('workflow.actions.approve', { state: pendingTo }),
                    action: () => {
                      setShowInlineMenu(false);
                      handleWorkflowApproval(selectedTradeForMenu);
                    },
                    disabled: processingWorkflow
                  });
                }
              }

              // Show available transitions or "no transitions available" message
              if (validTransitions.length === 0 && !hasPendingApproval) {
                // Add separator if we have previous items
                if (menuItems.length > 0) {
                  menuItems.push('separator');
                }
                menuItems.push({
                  label: t('workflow.messages.noTransitionsAvailable'),
                  action: () => {},
                  disabled: true,
                  className: 'info-text'
                });
              } else {
                validTransitions.forEach((targetState) => {
                  // Determine if this is a forward or revert transition
                  const isRevert = ['Draft'].includes(targetState) ||
                    (currentState === 'Confirmed' && targetState === 'Booked') ||
                    (currentState === 'ContractSigned' && targetState === 'Confirmed');

                  menuItems.push({
                    label: isRevert
                      ? t('workflow.actions.revertTo', { state: targetState })
                      : t('workflow.actions.advanceTo', { state: targetState }),
                    action: () => {
                      setShowInlineMenu(false);
                      handleWorkflowTransition(selectedTradeForMenu, targetState);
                    },
                    disabled: processingWorkflow || hasPendingApproval
                  });
                });
              }
            }

            // If no menu items, show appropriate message
            if (menuItems.length === 0) {
              if (isBackOfficeContext && !canPerformBackOfficeActions) {
                // User doesn't have permission (FO user on BO page)
                menuItems.push({
                  label: t('grid.contextMenu.noAccessToActions', 'View only - no actions available'),
                  action: () => setShowInlineMenu(false),
                  disabled: true,
                  className: 'info-text'
                });
              } else {
                // User has permission but no actions available for this trade/context
                menuItems.push({
                  label: t('grid.contextMenu.noActionsAvailable', 'No actions available'),
                  action: () => setShowInlineMenu(false),
                  disabled: true,
                  className: 'info-text'
                });
              }
            }

            // Return the menu items
            return menuItems;
          })()}
        />
      )}

      {/* Averaging progress popup for Forward Starting averaged-entry trades */}
      {showAveragingPopup && averagingPopupTrade && (
        <AveragingProgressPopup
          schedule={averagingPopupTrade.ForwardStartingFixingSchedule || []}
          forwardPoints={averagingPopupTrade.ForwardPoints || 0}
          finalPrice={averagingPopupTrade.Price || 0}
          position={averagingPopupPosition}
          onClose={() => setShowAveragingPopup(false)}
        />
      )}

      {/* Hidden file input for contract upload */}
      <input
        ref={contractFileInputRef}
        type="file"
        accept=".pdf,application/pdf"
        style={{ display: 'none' }}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file && selectedTradeForMenu) {
            handleContractUpload(file, selectedTradeForMenu);
          }
          e.target.value = ''; // Reset
        }}
      />

      {/* Uploading Modal */}
      <UploadingModal isOpen={uploadingContract} message={t('grid.messages.uploadingContract')} />

      {/* Deleting Modal */}
      <UploadingModal isOpen={deletingContract} message={t('grid.messages.deletingContract')} />

      {/* Workflow Processing Modal */}
      <UploadingModal isOpen={processingWorkflow} message={t('workflow.messages.processing')} />

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog(prev => ({ ...prev, isOpen: false }))}
      />
    </div>
  );
};

export default ClientTradesGrid;